/**
 * Which releases a category's products can be used in (Category.releaseTypes
 * on the backend). A category saved before the field existed allows all.
 * See multidental-backend/docs/category-release-types.md.
 */

export type CategoryReleaseKind = "patient" | "employee" | "clinic" | "treatment" | "prescription";

export const CATEGORY_RELEASE_KINDS: { value: CategoryReleaseKind; label: string; hint: string }[] = [
  { value: "patient", label: "Patient", hint: "Patient release" },
  { value: "employee", label: "Staff", hint: "Staff release" },
  { value: "clinic", label: "Clinic use", hint: "Clinic use release" },
  { value: "treatment", label: "Treatment", hint: "Products used on treatment stages" },
  { value: "prescription", label: "Prescription", hint: "Fill prescribed doses at billing" },
];

const ALL = CATEGORY_RELEASE_KINDS.map((k) => k.value);

/** The kinds a category allows; all of them when it has no list yet. */
export function allowedReleaseKinds(category: { releaseTypes?: unknown } | null | undefined): CategoryReleaseKind[] {
  if (!Array.isArray(category?.releaseTypes)) return [...ALL];
  const picked = new Set(category!.releaseTypes as string[]);
  return ALL.filter((k) => picked.has(k));
}

/** Tick / untick one kind, keeping the fixed order. */
export function toggleReleaseKind(list: CategoryReleaseKind[], kind: CategoryReleaseKind): CategoryReleaseKind[] {
  const next = new Set(list);
  if (next.has(kind)) next.delete(kind);
  else next.add(kind);
  return ALL.filter((k) => next.has(k));
}

/** "All releases", "Not used in any release", or "Patient, Clinic use". */
export function releaseKindsSummary(list: CategoryReleaseKind[]): string {
  if (list.length === ALL.length) return "All releases";
  if (!list.length) return "Not used in any release";
  return CATEGORY_RELEASE_KINDS.filter((k) => list.includes(k.value))
    .map((k) => k.label)
    .join(", ");
}
